import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, unwrap } from "./client";
import type { AuthConfig, Member, PageInfo, Role, SyncToken, User, Workspace, WorkspacePatch } from "./types";

export const qk = {
  me: ["me"] as const,
  workspaces: ["workspaces"] as const,
  workspace: (id: string) => ["workspace", id] as const,
  pages: (ws: string) => ["pages", ws] as const,
  page: (id: string) => ["page", id] as const,
  members: (ws: string) => ["members", ws] as const,
  trash: (ws: string) => ["trash", ws] as const,
  acl: (docId: string) => ["acl", docId] as const,
};

export async function fetchMe(): Promise<User | null> {
  const res = await api.GET("/api/auth/me");
  if (res.response.status === 401 || res.response.status === 403) return null;
  return unwrap<User>(res);
}

export function useMe() {
  return useQuery({ queryKey: qk.me, queryFn: fetchMe, staleTime: 60_000, retry: false });
}

/** [M23] The reader's interface language lives on their account (spec §8.23, Q60). */
export function useSetLanguage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (language: string) => unwrap<User>(await api.PATCH("/api/auth/me", { body: { language } })),
    onSuccess: (user) => qc.setQueryData(qk.me, user),
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { username: string; password: string }) =>
      unwrap<User>(await api.POST("/api/auth/login", { body })),
    onSuccess: (user) => qc.setQueryData(qk.me, user),
  });
}

/** What the login page offers (spec §19.1). Offline or on error: the password form alone. */
export function useAuthConfig() {
  return useQuery({
    queryKey: ["auth-config"],
    staleTime: 5 * 60_000,
    retry: false,
    queryFn: async (): Promise<AuthConfig> => unwrap<AuthConfig>(await api.GET("/api/auth/config")),
  });
}

/** Ends the session; resolves to Keycloak's end-session URL when the session came from OIDC. */
export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<string | null> => {
      const res = await api.POST("/api/auth/logout");
      return res.data?.endSessionUrl ?? null;
    },
    onSuccess: () => qc.clear(),
  });
}

export function useWorkspaces() {
  return useQuery({ queryKey: qk.workspaces, queryFn: async () => unwrap<Workspace[]>(await api.GET("/api/workspaces")) });
}

export function useWorkspace(id: string) {
  return useQuery({
    queryKey: qk.workspace(id),
    queryFn: async () => unwrap<Workspace>(await api.GET("/api/workspaces/{ws_id}", { params: { path: { ws_id: id } } })),
    // a deleted workspace answers 404 (spec §13.10); asking again will not bring it back
    retry: (count, e) => !(e instanceof ApiError && e.status === 404) && count < 3,
  });
}

/** [M20] Soft-delete a workspace; the owner types its name as the confirmation (spec §13.10). */
export function useDeleteWorkspace(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (name: string) => {
      const res = await api.DELETE("/api/workspaces/{ws_id}", { params: { path: { ws_id: ws } }, body: { name } });
      if (!res.response.ok) unwrap(res);
    },
    onSuccess: () => {
      qc.removeQueries({ queryKey: qk.workspace(ws) });
      void qc.invalidateQueries({ queryKey: qk.workspaces });
    },
  });
}

/** [M20] Restore a soft-deleted workspace; the server rebuilds its graph in the background. */
export function useRestoreWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ws: string) =>
      unwrap<Workspace>(await api.POST("/api/workspaces/{ws_id}/restore", { params: { path: { ws_id: ws } } })),
    onSuccess: (restored) => {
      qc.setQueryData(qk.workspace(restored.id), restored);
      void qc.invalidateQueries({ queryKey: qk.workspaces });
    },
  });
}

export function useCreateWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (name: string) => unwrap<Workspace>(await api.POST("/api/workspaces", { body: { name } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.workspaces }),
  });
}

// --- workspace settings (spec §8.22, M19) ---------------------------------------------------------

export function useMembers(ws: string) {
  return useQuery({
    queryKey: qk.members(ws),
    queryFn: async () => unwrap<Member[]>(await api.GET("/api/workspaces/{ws_id}/members", { params: { path: { ws_id: ws } } })),
  });
}

/** Rename the workspace or change one policy; absent fields are left alone. */
export function useUpdateWorkspace(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: WorkspacePatch) =>
      unwrap<Workspace>(await api.PATCH("/api/workspaces/{ws_id}", { params: { path: { ws_id: ws } }, body: patch })),
    onSuccess: (updated) => {
      qc.setQueryData(qk.workspace(ws), updated);
      void qc.invalidateQueries({ queryKey: qk.workspaces });
    },
  });
}

export function useAddMember(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { username: string; role: Role }) =>
      unwrap<Member>(await api.POST("/api/workspaces/{ws_id}/members", { params: { path: { ws_id: ws } }, body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.members(ws) }),
  });
}

export function useChangeRole(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: Role }) =>
      unwrap<Member>(
        await api.PATCH("/api/workspaces/{ws_id}/members/{user_id}", { params: { path: { ws_id: ws, user_id: userId } }, body: { role } }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.members(ws) }),
  });
}

export function useRemoveMember(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      const res = await api.DELETE("/api/workspaces/{ws_id}/members/{user_id}", { params: { path: { ws_id: ws, user_id: userId } } });
      if (!res.response.ok) unwrap(res);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.members(ws) }),
  });
}

// --- trash (spec §8.22 "Trash", §13.9) --------------------------------------------------------------

export function useTrash(ws: string, enabled = true) {
  return useQuery({
    queryKey: qk.trash(ws),
    enabled,
    queryFn: async () => unwrap<PageInfo[]>(await api.GET("/api/workspaces/{ws_id}/trash", { params: { path: { ws_id: ws } } })),
  });
}

export function useDeletePage(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (docId: string) => {
      const res = await api.DELETE("/api/pages/{doc_id}", { params: { path: { doc_id: docId } } });
      if (!res.response.ok) unwrap(res);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.pages(ws) });
      void qc.invalidateQueries({ queryKey: qk.trash(ws) });
    },
  });
}

export function useRestorePage(ws: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (docId: string) => unwrap<PageInfo>(await api.POST("/api/pages/{doc_id}/restore", { params: { path: { doc_id: docId } } })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.pages(ws) });
      void qc.invalidateQueries({ queryKey: qk.trash(ws) });
    },
  });
}

// --- per-page write restrictions (spec §8.22, §10.2) -------------------------------------------------

export function useAcl(docId: string, enabled = true) {
  return useQuery({
    queryKey: qk.acl(docId),
    enabled,
    queryFn: async () => unwrap<{ restricted_user_ids: string[] }>(await api.GET("/api/pages/{doc_id}/acl", { params: { path: { doc_id: docId } } })),
  });
}

export function useSetAcl(docId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (restricted_user_ids: string[]) =>
      unwrap<{ restricted_user_ids: string[] }>(
        await api.PUT("/api/pages/{doc_id}/acl", { params: { path: { doc_id: docId } }, body: { restricted_user_ids } }),
      ),
    onSuccess: (acl) => qc.setQueryData(qk.acl(docId), acl),
  });
}

export function usePages(ws: string) {
  return useQuery({
    queryKey: qk.pages(ws),
    queryFn: async () => unwrap<PageInfo[]>(await api.GET("/api/workspaces/{ws_id}/pages", { params: { path: { ws_id: ws } } })),
  });
}

export function usePage(id: string) {
  return useQuery({
    queryKey: qk.page(id),
    queryFn: async () => unwrap<PageInfo>(await api.GET("/api/pages/{doc_id}", { params: { path: { doc_id: id } } })),
  });
}

export async function registerPage(ws: string, id: string, title: string): Promise<PageInfo> {
  return unwrap<PageInfo>(await api.POST("/api/workspaces/{ws_id}/pages", { params: { path: { ws_id: ws } }, body: { id, title } }));
}

export async function fetchSyncToken(room: string, peerId: string, device: string): Promise<SyncToken> {
  return unwrap<SyncToken>(await api.POST("/api/sync/token", { body: { room, peerId, device } }));
}

// --- graph, ontology and search (M4) ----------------------------------------------------------

export interface TermHit {
  iri: string;
  label: string;
  kind: string;
  definition?: string | null;
  deprecated?: boolean;
  matchedLabel?: string | null;
}
export interface PredicateHit {
  iri: string;
  label: string;
  source: string;
  subjectClass?: string | null;
  objectClass?: string | null;
  /** [M18] set when the predicate takes a literal of this datatype (spec §14.6.1) */
  objectDatatype?: string | null;
  definition?: string | null;
}
export interface BlockHit {
  iri: string;
  label: string;
  blockType: string;
  docId: string;
  treeId: string;
  docTitle: string;
}
export interface SparqlBinding {
  [name: string]: { type: string; value: string; datatype?: string; "xml:lang"?: string } | undefined;
}
export interface QueryResult {
  kind: string;
  results: { head?: { vars?: string[] }; results?: { bindings: SparqlBinding[] }; boolean?: boolean; ntriples?: string };
  truncated: boolean;
  rowCount: number;
  elapsedMs: number;
}

export function usePredicates(subjectType: string | null, enabled = true) {
  return useQuery({
    queryKey: ["predicates", subjectType],
    enabled: enabled && !!subjectType,
    staleTime: 5 * 60_000,
    queryFn: async () =>
      unwrap<PredicateHit[]>(await api.GET("/api/ontology/predicates", { params: { query: { subjectType: subjectType ?? "" } } })),
  });
}

/** Ontology term search (spec §8.11). `ns` or `types` narrow it; with either, an empty query lists the matches. */
export function useTermSearch(
  q: string,
  enabled = true,
  filter: { ns?: string; types?: string[]; limit?: number; kind?: "class" | "property" | "individual" } = {},
) {
  const { ns = "", types = [], limit = 20, kind } = filter;
  return useQuery({
    queryKey: ["terms", q, ns, types.join(","), limit, kind ?? ""],
    enabled: enabled && (q.trim().length > 0 || ns.length > 0 || types.length > 0),
    staleTime: 60_000,
    queryFn: async () =>
      unwrap<TermHit[]>(await api.GET("/api/ontology/terms", { params: { query: { q, limit, ns, types: types.join(","), kind } } })),
  });
}

export function useBlockSearch(workspaceId: string, q: string, enabled = true, includePages = false) {
  return useQuery({
    queryKey: ["searchBlocks", workspaceId, q, includePages],
    enabled,
    staleTime: 30_000,
    queryFn: async () =>
      unwrap<BlockHit[]>(await api.GET("/api/search/blocks", { params: { query: { workspaceId, q, limit: 20, includePages } } })),
  });
}

export async function expandNode(workspaceId: string, iri: string, direction: "in" | "out" | "both" = "in", limit = 100): Promise<QueryResult> {
  return unwrap<QueryResult>(await api.POST("/api/graph/expand", { body: { workspaceId, iri, direction, limit } }));
}

export function useIncomingLinks(workspaceId: string, iri: string | null) {
  return useQuery({
    queryKey: ["incoming", workspaceId, iri],
    enabled: !!iri,
    staleTime: 15_000,
    retry: false,
    queryFn: async () => expandNode(workspaceId, iri!, "in", 100),
  });
}

export async function describeNodes(workspaceId: string, iris: string[]): Promise<Array<{ iri: string; label: string; kind: string; displayStyle?: string | null; blockType?: string | null; thumbnailUrl?: string | null; types: string[] }>> {
  if (iris.length === 0) return [];
  return unwrap(await api.POST("/api/graph/describe-nodes", { body: { workspaceId, iris, langs: ["en"] } }));
}

/** Derived graphs whose source block changed since extraction (spec §18.6). Online only. */
export function useStaleGraphs(workspaceId: string) {
  return useQuery({
    queryKey: ["staleGraphs", workspaceId],
    staleTime: 30_000,
    retry: false,
    queryFn: async () => {
      const out = unwrap<{ graphs: string[] }>(await api.GET("/api/graph/stale-graphs", { params: { query: { workspaceId } } }));
      return new Set(out.graphs);
    },
  });
}

export function usePrefixes() {
  return useQuery({
    queryKey: ["prefixes"],
    staleTime: 10 * 60_000,
    queryFn: async () => unwrap<Record<string, string>>(await api.GET("/api/ontology/prefixes")),
  });
}
